import { DateTime } from 'luxon';
import { getAvatarUrl, getProfilePictureUrl } from '../utils/cloudinary';

const TIMEZONE = 'America/New_York';

/**
 * Get profile picture URL from a person's profilePictureUrl field
 * Handles both legacy URLs and Cloudinary public IDs
 *
 * @param {Object} person - Person object with profilePictureUrl field
 * @param {number} size - Desired size (default: 100 for avatars)
 * @returns {string|null} The image URL or null
 */
export function getPersonAvatarUrl(person, size = 100) {
  if (!person?.profilePictureUrl) return null;
  return getAvatarUrl(person.profilePictureUrl, size);
}

/**
 * Get larger profile picture URL for detail views
 *
 * @param {Object} person - Person object with profilePictureUrl field
 * @param {number} size - Desired size (default: 400)
 * @returns {string|null} The image URL or null
 */
export function getPersonProfilePictureUrl(person, size = 400) {
  if (!person?.profilePictureUrl) return null;
  return getProfilePictureUrl(person.profilePictureUrl, size);
}

/**
 * Get display name for a person
 */
export function getDisplayName(person) {
  if (!person) return '';
  const preferred = person.preferredName;
  const fullName = `${person.firstName} ${person.lastName}`;
  return preferred ? `${preferred} (${fullName})` : fullName;
}

/**
 * Get short display name (preferred or first name)
 */
export function getShortName(person) {
  if (!person) return '';
  return person.preferredName || person.firstName;
}

/**
 * Calculate age from birthday
 */
export function calculateAge(birthday, deceasedDate = null) {
  if (!birthday) return null;
  
  const birthDate = DateTime.fromISO(birthday, { zone: TIMEZONE });
  const endDate = deceasedDate 
    ? DateTime.fromISO(deceasedDate, { zone: TIMEZONE })
    : DateTime.now().setZone(TIMEZONE);
  
  const age = Math.floor(endDate.diff(birthDate, 'years').years);
  return age >= 0 ? age : null;
}

/**
 * Get days until next birthday
 */
export function getDaysUntilBirthday(birthday) {
  if (!birthday) return null;
  
  const now = DateTime.now().setZone(TIMEZONE);
  const birthDate = DateTime.fromISO(birthday, { zone: TIMEZONE });
  let nextBirthday = birthDate.set({ year: now.year });
  
  if (nextBirthday < now.startOf('day')) {
    nextBirthday = nextBirthday.set({ year: now.year + 1 });
  }
  
  return Math.ceil(nextBirthday.diff(now.startOf('day'), 'days').days);
}

/**
 * Format birthday for display
 */
export function formatBirthday(birthday) {
  if (!birthday) return '';
  const date = DateTime.fromISO(birthday, { zone: TIMEZONE });
  return date.toFormat('MMMM d, yyyy');
}

/**
 * Format date for display
 */
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = DateTime.fromISO(dateString, { zone: TIMEZONE });
  return date.toFormat('MMMM d, yyyy');
}

/**
 * Format datetime for display
 */
export function formatDateTime(dateString) {
  if (!dateString) return '';

  let date;
  // Check if it's a numeric timestamp (milliseconds)
  if (!isNaN(dateString) && !isNaN(parseInt(dateString))) {
    date = DateTime.fromMillis(parseInt(dateString), { zone: TIMEZONE });
  } else {
    date = DateTime.fromISO(dateString, { zone: TIMEZONE });
  }

  if (!date.isValid) return '';
  return date.toFormat('MMM d, yyyy h:mm a');
}

/**
 * Get initials for avatar
 */
export function getInitials(person) {
  if (!person) return '?';
  const first = (person.preferredName || person.firstName || '').charAt(0);
  const last = (person.lastName || '').charAt(0);
  return (first + last).toUpperCase();
}

/**
 * Generate vCard content
 */
export function generateVCard(person) {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${person.lastName};${person.firstName};${person.middleName || ''};`,
    `FN:${person.firstName}${person.middleName ? ' ' + person.middleName : ''} ${person.lastName}`,
  ];

  if (person.preferredName) {
    lines.push(`NICKNAME:${person.preferredName}`);
  }
  if (person.phone) {
    lines.push(`TEL;TYPE=CELL:${person.phone}`);
  }
  if (person.email) {
    lines.push(`EMAIL:${person.email}`);
  }
  if (person.birthday) {
    const bday = DateTime.fromISO(person.birthday).toFormat('yyyyMMdd');
    lines.push(`BDAY:${bday}`);
  }
  if (person.household?.addressLine1) {
    const h = person.household;
    const addr = [
      '',
      '',
      h.addressLine1,
      h.city || '',
      h.state || '',
      h.postalCode || '',
      h.country || 'USA',
    ].join(';');
    lines.push(`ADR;TYPE=HOME:${addr}`);
  }

  lines.push('END:VCARD');
  return lines.join('\r\n');
}

/**
 * Download vCard file
 */
export function downloadVCard(person) {
  const vcard = generateVCard(person);
  const blob = new Blob([vcard], { type: 'text/vcard' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${person.firstName}_${person.lastName}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default {
  getDisplayName,
  getShortName,
  calculateAge,
  getDaysUntilBirthday,
  formatBirthday,
  formatDate,
  formatDateTime,
  getInitials,
  generateVCard,
  downloadVCard,
};

